import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { apiClient } from '../../utils/apiClient';
import './TokenSubmissionManager.css';

const TokenSubmissionManager = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { isDark } = useTheme();
  
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingSubmission, setProcessingSubmission] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (user && isAdmin()) {
      fetchSubmissions();
      fetchStats();
    }
  }, [user]);

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.isPoolManager);
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/token-submissions');
      
      if (response.success) {
        setSubmissions(response.submissions || []);
      } else {
        throw new Error('Failed to fetch submissions');
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showError('Failed to load token submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/token-submissions/stats');
      
      if (response.success) {
        setStats(response.stats || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApproveSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      setProcessingSubmission(selectedSubmission.id);
      
      const response = await apiClient.post(`/admin/token-submissions/${selectedSubmission.id}/approve`, {
        adminNotes: adminNotes.trim() || null
      });

      if (response.success) {
        showSuccess('Token approved and added to listings!');
        setShowApprovalModal(false);
        setAdminNotes('');
        setSelectedSubmission(null);
        await fetchSubmissions();
        await fetchStats();
      } else {
        throw new Error(response.message || 'Failed to approve token');
      }
    } catch (error) {
      console.error('Error approving submission:', error);
      showError(error.message || 'Failed to approve token');
    } finally {
      setProcessingSubmission(null);
    }
  };

  const handleRejectSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      setProcessingSubmission(selectedSubmission.id);
      
      const response = await apiClient.post(`/admin/token-submissions/${selectedSubmission.id}/reject`, {
        rejectionReason: rejectionReason.trim()
      });

      if (response.success) {
        showSuccess('Token submission rejected');
        setShowRejectionModal(false);
        setRejectionReason('');
        setSelectedSubmission(null);
        await fetchSubmissions();
        await fetchStats();
      } else {
        throw new Error(response.message || 'Failed to reject token');
      }
    } catch (error) {
      console.error('Error rejecting submission:', error);
      showError(error.message || 'Failed to reject token');
    } finally {
      setProcessingSubmission(null);
    }
  };

  const openApprovalModal = (submission) => {
    setSelectedSubmission(submission);
    setAdminNotes('');
    setShowApprovalModal(true);
  };

  const openRejectionModal = (submission) => {
    setSelectedSubmission(submission);
    setRejectionReason('');
    setShowRejectionModal(true);
  };

  const closeModals = () => {
    setShowApprovalModal(false);
    setShowRejectionModal(false);
    setSelectedSubmission(null);
    setAdminNotes('');
    setRejectionReason('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPrice = (price) => {
    if (price === 0) return 'N/A';
    if (price < 0.01) return `$${price.toFixed(8)}`;
    return `$${price.toFixed(4)}`;
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap === 0) return 'N/A';
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toLocaleString()}`;
  };

  if (!isAdmin()) {
    return (
      <div className="token-submission-manager">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="token-submission-manager">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading token submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`token-submission-manager ${isDark ? 'dark' : 'light'}`}>
      <div className="header">
        <div className="header-info">
          <h1>Token Submissions</h1>
          <p>Review and approve user-submitted tokens</p>
        </div>
        <button 
          className="refresh-button"
          onClick={() => {
            fetchSubmissions();
            fetchStats();
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total_submissions || 0}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{stats.pending_submissions || 0}</div>
            <div className="stat-label">Pending Review</div>
          </div>
        </div>
        <div className="stat-card approved">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.approved_submissions || 0}</div>
            <div className="stat-label">Approved</div>
          </div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-value">{stats.rejected_submissions || 0}</div>
            <div className="stat-label">Rejected</div>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      {submissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No Pending Submissions</h3>
          <p>All token submissions have been reviewed</p>
        </div>
      ) : (
        <div className="submissions-table">
          <div className="table-header">
            <div className="col-token">Token</div>
            <div className="col-submitter">Submitted By</div>
            <div className="col-date">Submitted</div>
            <div className="col-actions">Actions</div>
          </div>
          
          {submissions.map((submission) => (
            <div key={submission.id} className="table-row">
              <div className="col-token">
                <div className="token-info">
                  {submission.logo_uri && (
                    <img 
                      src={submission.logo_uri} 
                      alt={submission.name}
                      className="token-image"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="token-details">
                    <div className="token-name">{submission.name || 'Unknown Token'}</div>
                    <div className="token-symbol">{submission.symbol || 'UNKNOWN'}</div>
                    <div className="token-address">
                      {submission.mint_address.slice(0, 8)}...{submission.mint_address.slice(-8)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-submitter">
                <div className="submitter-info">
                  <div className="submitter-email">{submission.submitted_by.email}</div>
                  {submission.submitted_by.username && (
                    <div className="submitter-username">@{submission.submitted_by.username}</div>
                  )}
                </div>
              </div>
              
              <div className="col-date">
                {formatDate(submission.created_at)}
              </div>
              
              <div className="col-actions">
                <button 
                  className="approve-button"
                  onClick={() => openApprovalModal(submission)}
                  disabled={processingSubmission === submission.id}
                >
                  ✅ Approve
                </button>
                <button 
                  className="reject-button"
                  onClick={() => openRejectionModal(submission)}
                  disabled={processingSubmission === submission.id}
                >
                  ❌ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedSubmission && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Approve Token Submission</h2>
              <button className="modal-close" onClick={closeModals}>×</button>
            </div>
            <div className="modal-body">
              <div className="submission-preview">
                <h3>{selectedSubmission.name || 'Unknown Token'}</h3>
                <p><strong>Symbol:</strong> {selectedSubmission.symbol || 'UNKNOWN'}</p>
                <p><strong>Address:</strong> {selectedSubmission.mint_address}</p>
                {selectedSubmission.description && (
                  <p><strong>Description:</strong> {selectedSubmission.description}</p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="adminNotes">Admin Notes (Optional)</label>
                <textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any notes about this approval..."
                  rows="3"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={closeModals}
                  disabled={processingSubmission === selectedSubmission.id}
                >
                  Cancel
                </button>
                <button 
                  className="approve-button"
                  onClick={handleApproveSubmission}
                  disabled={processingSubmission === selectedSubmission.id}
                >
                  {processingSubmission === selectedSubmission.id ? 'Approving...' : 'Approve Token'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedSubmission && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Token Submission</h2>
              <button className="modal-close" onClick={closeModals}>×</button>
            </div>
            <div className="modal-body">
              <div className="submission-preview">
                <h3>{selectedSubmission.name || 'Unknown Token'}</h3>
                <p><strong>Symbol:</strong> {selectedSubmission.symbol || 'UNKNOWN'}</p>
                <p><strong>Address:</strong> {selectedSubmission.mint_address}</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="rejectionReason">Rejection Reason *</label>
                <textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows="3"
                  required
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={closeModals}
                  disabled={processingSubmission === selectedSubmission.id}
                >
                  Cancel
                </button>
                <button 
                  className="reject-button"
                  onClick={handleRejectSubmission}
                  disabled={processingSubmission === selectedSubmission.id || !rejectionReason.trim()}
                >
                  {processingSubmission === selectedSubmission.id ? 'Rejecting...' : 'Reject Token'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenSubmissionManager;
